import React, { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ExternalLink, ArrowLeft } from "lucide-react";
import PageShell from "@/components/PageShell";
import EmailCapture from "@/components/EmailCapture";
import PromoRibbon, { withCoupon } from "@/components/PromoRibbon";
import { digitalProducts } from "@/data/enrichments";
import { openCheckout } from "@/lib/lemonsqueezy";
import { trackEvent, installEngagementTracking } from "@/lib/tracking";
import { addUtm } from "@/lib/utm";

// Per-painting landing page — the ad destination for the art vertical.
// Mirrors BookDetail's funnel anatomy: view_content on mount, engagement
// tracking installed, shop_buy_click on every storefront exit. An ad click
// lands on THE painting advertised, not the gallery grid.

function StorefrontRow({ product, sf, index }) {
  if (!sf.url) {
    return (
      <span className="btn-pill opacity-60 cursor-default" title="Coming soon">
        {sf.label} · soon
      </span>
    );
  }
  const stampedUrl = withCoupon(addUtm(sf.url, {
    campaign: `shop-${product.id}`,
    content: sf.store,
  }), product.id);
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <a
        href={stampedUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackEvent("shop_buy_click", {
            product_slug: product.id,
            product_kind: product.kind,
            store: sf.store,
            outbound_url: stampedUrl,
            title_id: product.id,
          })
        }
        className="btn-shop"
        data-track="shop-storefront-out"
        data-store={sf.store}
      >
        {sf.label}
        <ExternalLink size={11} strokeWidth={1.5} />
      </a>
      <div className="font-body text-[0.7rem] text-ink-mute leading-tight min-w-0">
        <span className="text-ink">{sf.price_text}</span>
        <span className="mx-1.5 text-ink-mute/50">·</span>
        <span>{sf.sublabel}</span>
      </div>
    </div>
  );
}

function CrossSell({ current }) {
  const others = digitalProducts.filter((p) => p.id !== current.id).slice(0, 3);
  if (!others.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-5 md:px-10 pb-20">
      <p className="eyebrow mb-6">More from the collection</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {others.map((p) => (
          <Link
            key={p.id}
            to={`/shop/${p.id}`}
            onClick={() =>
              trackEvent("cross_sell_click", {
                from_title: current.id,
                to_title: p.id,
                title_id: current.id,
              })
            }
            className="group block"
          >
            <div className={`card-shrine rounded-sm overflow-hidden ${p.orientation === "landscape" ? "aspect-[3/2]" : "aspect-[4/5]"} bg-shrine-shadow`}>
              <img
                src={p.hero_image}
                alt={p.title}
                className="w-full h-full object-cover transition-transform duration-1000 ease-breath group-hover:scale-[1.02]"
                loading="lazy"
              />
            </div>
            <h3 className="font-display text-lg text-ink mt-3 leading-tight">{p.title}</h3>
            <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase mt-1">
              {p.subtitle}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ShopDetail() {
  const { slug } = useParams();
  const product = digitalProducts.find((p) => p.id === slug);

  // view_content once per unique slug — same ref-guard as BookDetail (the
  // effect can re-run under StrictMode/Framer remounts; the ref keeps the
  // funnel event idempotent).
  const firedFor = useRef(null);
  useEffect(() => {
    if (!product) return;
    if (firedFor.current === slug) return;
    firedFor.current = slug;
    window.scrollTo(0, 0);
    trackEvent("view_content", {
      title_id: product.id,
      slug: product.id,
      hero_image: product.hero_image,
      currency: "USD",
      value: 0,
    });
    return installEngagementTracking({ title_id: product.id, slug: product.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!product) {
    return (
      <PageShell title="Not found">
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-3xl text-ink">Piece not found</h1>
          <Link to="/shop" className="btn-pill inline-flex items-center gap-2 mt-8">
            <ArrowLeft size={14} /> Back to the shop
          </Link>
        </div>
      </PageShell>
    );
  }

  const hasStorefronts =
    Array.isArray(product.external_storefronts) && product.external_storefronts.length > 0;

  return (
    <PageShell title={product.title}>
      {/* HERO — the painting, full presence */}
      <section className="bg-cream pt-10 md:pt-16 pb-12 md:pb-20">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <Link
            to="/shop"
            className="font-body text-[0.72rem] text-ink-mute tracking-widest2 uppercase inline-flex items-center gap-2 mb-8 hover:text-ink transition-colors"
          >
            <ArrowLeft size={12} /> Shop
          </Link>
          <div className={`grid grid-cols-1 ${product.orientation === "landscape" ? "" : "md:grid-cols-2"} gap-10 md:gap-14 items-start`}>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
              className={`card-shrine rounded-sm overflow-hidden ${product.orientation === "landscape" ? "aspect-[3/2]" : "aspect-[4/5]"} bg-shrine-shadow`}
            >
              <img
                src={product.hero_image}
                alt={product.title}
                className="w-full h-full object-cover"
              />
            </motion.div>

            <div className={product.orientation === "landscape" ? "max-w-2xl" : ""}>
              <p className="eyebrow mb-3">{product.subtitle}</p>
              <h1 className="font-display text-3xl md:text-5xl text-ink leading-tight tracking-tightish">
                {product.title}
              </h1>
              <p className="font-serif text-ink-soft text-lg md:text-xl mt-6 leading-relaxed">
                {product.description}
              </p>

              <div className="-mx-5 md:-mx-10 mb-2"><PromoRibbon where="shop-detail" /></div>
              {/* Buy options — every exit instrumented */}
              {hasStorefronts ? (
                <div className="mt-8 space-y-3">
                  {product.external_storefronts.map((sf, i) => (
                    <StorefrontRow key={sf.store} product={product} sf={sf} index={i} />
                  ))}
                </div>
              ) : (
                <button
                  onClick={() => {
                    trackEvent("shop_buy_click", {
                      product_slug: product.id,
                      product_kind: product.kind,
                      store: "dharmaweave_ls",
                      price_usd: product.price_usd,
                      title_id: product.id,
                    });
                    openCheckout(product);
                  }}
                  className="inline-flex items-center gap-2 mt-8 btn-pill btn-pill-solid"
                >
                  <ShoppingBag size={14} />
                  Buy — ${product.price_usd?.toFixed(2)}
                </button>
              )}

              <p className="font-serif italic text-ink-mute text-sm mt-6">
                Printed on premium matte, shipped tracked. Painted in the sacred
                art tradition — devotional, dignified, never neon.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CrossSell current={product} />

      <section className="bg-cream pb-24">
        <div className="max-w-md mx-auto px-5">
          <EmailCapture
            titleId={product.id}
            slug={product.id}
            prompt="New pieces, once in a while."
            success="We'll write when the next piece is ready. Thank you."
          />
        </div>
      </section>
    </PageShell>
  );
}
